/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */

import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {BranchPicker} from '@github-ui/item-picker-branch'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useFragment, useRelayEnvironment} from 'react-relay'
import {fetchQuery, graphql, readInlineData} from 'relay-runtime'

import type {BranchPickerSharedRef$data, BranchPickerSharedRef$key} from './__generated__/BranchPickerSharedRef.graphql'
import type {BranchPickerSharedRepositoryBranches$key} from './__generated__/BranchPickerSharedRepositoryBranches.graphql'
import type {BranchPickerSharedRepositoryBranchRefs$key} from './__generated__/BranchPickerSharedRepositoryBranchRefs.graphql'
import type {BranchPickerSharedSearchBranchesFragment$key} from './__generated__/BranchPickerSharedSearchBranchesFragment.graphql'
import type {
  BranchPickerSharedSearchBranchesQuery,
  BranchPickerSharedSearchBranchesQuery$data,
} from './__generated__/BranchPickerSharedSearchBranchesQuery.graphql'

export type BranchPickerBranch = Omit<BranchPickerSharedRef$data, ' $fragmentType'>

/**
 * Helper function to read BranchPickerRepositoryBranchRefs data from a Relay key
 */
export function readBranchPickerRepositoryBranchRefs(key: BranchPickerSharedRepositoryBranchRefs$key | null) {
  if (!key) return null
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData(BranchPickerSharedRepositoryBranchRefsFragment, key)
}

/**
 * Helper function to read BranchPickerRef data from a Relay key and return as BranchPickerBranch
 */
export function readBranchPickerRef(key: BranchPickerSharedRef$key | null): BranchPickerBranch | null {
  if (!key) return null
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData(BranchPickerSharedRefFragment, key)
}

export function readBranchPickerRepositoryBranches(key: BranchPickerSharedRepositoryBranches$key | null) {
  if (!key) return null
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData(BranchPickerSharedRepositoryBranchesFragment, key)
}

export type BranchPickerInternalProps = {
  initialBranch: BranchPickerBranch | null | undefined
  defaultBranchId: string | undefined
  branches: BranchPickerBranch[] | null
  onSelect: (branch: BranchPickerBranch | undefined) => void
  enforceAtleastOneSelected?: boolean
  owner: string
  repo: string
  title?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

export const BranchPickerSharedRepositoryBranchesGraphqlQuery = graphql`
  query BranchPickerSharedRepositoryBranchesQuery($owner: String!, $name: String!) {
    repository(owner: $owner, name: $name) {
      ...BranchPickerSharedRepositoryBranchRefs
    }
  }
`

const BranchPickerSharedRepositoryBranchRefsFragment = graphql`
  fragment BranchPickerSharedRepositoryBranchRefs on Repository @inline {
    id
    defaultBranchRef {
      ...BranchPickerSharedRef
    }
    refs(refPrefix: "refs/heads/", first: 25) {
      ...BranchPickerSharedRepositoryBranches
    }
  }
`

const BranchPickerSharedRepositoryBranchesFragment = graphql`
  fragment BranchPickerSharedRepositoryBranches on RefConnection @inline {
    edges {
      node {
        ...BranchPickerSharedRef
      }
    }
  }
`

const BranchPickerSharedRefFragment = graphql`
  fragment BranchPickerSharedRef on Ref @inline {
    name
    id
    __typename
    target {
      oid
      id
      __typename
    }
    repository {
      id
      nameWithOwner
      defaultBranchRef {
        name
        id
        target {
          oid
          id
          __typename
        }
        associatedPullRequests {
          totalCount
        }
        repository {
          id
        }
      }
    }
    associatedPullRequests {
      totalCount
    }
  }
`

export const BranchPickerSharedSearchBranches = graphql`
  query BranchPickerSharedSearchBranchesQuery($owner: String!, $name: String!, $first: Int!, $query: String) {
    ...BranchPickerSharedSearchBranchesFragment
  }
`

export const BranchPickerSharedSearchBranchesFragment = graphql`
  fragment BranchPickerSharedSearchBranchesFragment on Query {
    repository(owner: $owner, name: $name) {
      refs(refPrefix: "refs/heads/", first: $first, query: $query) {
        edges {
          node {
            ...BranchPickerSharedRef
          }
        }
      }
    }
  }
`

export function BranchPickerInternal({
  initialBranch,
  defaultBranchId,
  branches: inputBranches,
  owner,
  repo,
  onSelect,
  title,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: BranchPickerInternalProps) {
  const [searchResultsFragment, setSearchResultsFragment] = useState<BranchPickerSharedSearchBranchesQuery$data | null>(
    null,
  )
  const [searchResults, setSearchResults] = useState<BranchPickerBranch[] | undefined>(undefined)
  const [searchLoading, setSearchLoading] = useState<boolean>(false)
  const {addToast} = useToastContext()

  const searchedBranchesData = useFragment<BranchPickerSharedSearchBranchesFragment$key>(
    BranchPickerSharedSearchBranchesFragment,
    searchResultsFragment,
  )

  const suggestedBranches = useMemo(() => {
    if (!searchedBranchesData) {
      return undefined
    }
    return (searchedBranchesData?.repository?.refs?.edges || []).flatMap(edge =>
      // eslint-disable-next-line no-restricted-syntax
      edge?.node ? [readInlineData<BranchPickerSharedRef$key>(BranchPickerSharedRefFragment, edge.node)] : [],
    )
  }, [searchedBranchesData])

  useEffect(() => {
    setSearchResults(suggestedBranches)
  }, [suggestedBranches])

  const branches = useMemo(() => {
    const repoBranches =
      (inputBranches || [])

        .filter((branch): branch is BranchPickerBranch => Boolean(branch))
        .filter(branch => branch && branch.id !== initialBranch?.id) ?? []

    if (initialBranch) {
      repoBranches.unshift(initialBranch)
    }
    return repoBranches
  }, [inputBranches, initialBranch])

  const items = useMemo(() => {
    if (searchResults) {
      return searchResults
    }
    return branches
  }, [branches, searchResults])

  const environment = useRelayEnvironment()

  const fetchSearchData = useCallback(
    (query: string) => {
      if (!query) {
        setSearchResults(undefined)
        return
      }

      setSearchLoading(true)

      fetchQuery<BranchPickerSharedSearchBranchesQuery>(environment, BranchPickerSharedSearchBranches, {
        owner,
        name: repo,
        query,
        first: 25,
      }).subscribe({
        next: (data: BranchPickerSharedSearchBranchesQuery$data) => {
          if (data !== null) {
            setSearchResultsFragment(data)
          }
          setSearchLoading(false)
        },
        error: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotSearchAssignees,
          })
          setSearchLoading(false)
        },
      })
    },
    [addToast, environment, owner, repo],
  )

  return (
    <BranchPicker
      items={items}
      fetchSearchData={fetchSearchData}
      initialSelectedItem={initialBranch}
      defaultBranchId={defaultBranchId}
      onSelectionChange={onSelect}
      loading={searchLoading}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      title={title}
    />
  )
}

try{ BranchPickerInternal.displayName ||= 'BranchPickerInternal' } catch {}